/* RuleLibrary — read-mostly catalog grid.
 *
 * Spec 07 §"Library". Per-card actions:
 *   • checkbox toggles selection (batch promote at the bottom of the page)
 *   • "Open in builder" navigates with ?from=<id> for tweaking
 *
 * The per-card "Add to project" verb is gone. One verb: promote.
 * The Builder is where authoring happens; this is the catalog only.
 */

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Filter, BookOpen, Check, Wrench, ShieldCheck } from "lucide-react";
import {
  DESIGNER_RULES,
  CATEGORY_LABEL,
  CATEGORY_COLOR,
  type DesignerRule,
  type RuleCategory,
} from "../../data/designerRules";
import { predicateToText } from "../../lib/predicateRender";
import { cls } from "../../lib/cls";

const ALL_CATEGORIES: (RuleCategory | "all")[] = [
  "all",
  "cultural",
  "spatial",
  "budget",
  "timeline",
  "style",
  "vendor",
  "approval",
  "accessibility",
];

/* A rule is statutory when its `dataset` cites a published code /
 * regulation (NBC India 2016, ECBC 2017, RERA Act 2016 §3, IGBC
 * Green Homes, GRIHA, ANSI A117.1). NKBA / AIA are conventions, not
 * statutory. Match is substring-based so future amendments still
 * surface (e.g. "NBC 2026 Part 3"). */
const STATUTORY_CITES = ["NBC", "ECBC", "RERA", "IGBC", "GRIHA", "ANSI"] as const;
function isStatutory(r: DesignerRule): boolean {
  if (!r.dataset) return false;
  const d = r.dataset.toUpperCase();
  return STATUTORY_CITES.some((c) => d.includes(c));
}

interface RuleLibraryProps {
  promotedIds: ReadonlySet<string>;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (rule: DesignerRule) => void;
  onOpenInBuilder: (rule: DesignerRule) => void;
}

export function RuleLibrary({ promotedIds, selectedIds, onToggleSelect, onOpenInBuilder }: RuleLibraryProps): JSX.Element {
  const [filter, setFilter] = useState<RuleCategory | "all">("all");
  const [statutoryOnly, setStatutoryOnly] = useState(false);

  const statutoryCount = useMemo(() => DESIGNER_RULES.filter(isStatutory).length, []);

  const rules: DesignerRule[] = useMemo(() => {
    let r = DESIGNER_RULES;
    if (filter !== "all") r = r.filter((x) => x.category === filter);
    if (statutoryOnly) r = r.filter(isStatutory);
    return r;
  }, [filter, statutoryOnly]);

  return (
    <div className="space-y-4">
      <Header promotedCount={promotedIds.size} />

      <div className="panel p-3 sm:p-4 flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-ink-500" />
        <span className="eyebrow mr-1">Filter</span>
        {ALL_CATEGORIES.map((c) => {
          const active = c === filter;
          const count = c === "all" ? DESIGNER_RULES.length : DESIGNER_RULES.filter((r) => r.category === c).length;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={cls(
                "pill border transition-all",
                active
                  ? "bg-ink-950 text-cream border-ink-950"
                  : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
              )}
            >
              {c === "all" ? "All" : CATEGORY_LABEL[c]}
              <span className="opacity-60 ml-1 tabular">{count}</span>
            </button>
          );
        })}
        <span className="w-px h-4 bg-border mx-1" />
        <button
          onClick={() => setStatutoryOnly((v) => !v)}
          title="Only rules grounded in published codes — NBC 2016 · ECBC 2017 · RERA 2016 §3 · IGBC · GRIHA · ANSI A117.1"
          className={cls(
            "pill border transition-all inline-flex items-center gap-1",
            statutoryOnly
              ? "bg-foyr text-cream border-foyr"
              : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
          )}
        >
          <ShieldCheck className="w-3 h-3" />
          Statutory only
          <span className={cls("ml-1 tabular", statutoryOnly ? "opacity-80" : "opacity-60")}>{statutoryCount}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rules.map((r, i) => {
          const promoted = promotedIds.has(r.id);
          const selected = selectedIds.has(r.id);
          return (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: i * 0.02 }}
              onClick={() => onToggleSelect(r)}
              className={cls(
                "panel p-4 flex flex-col gap-2 transition-all cursor-pointer",
                selected ? "ring-2 ring-foyr/60" : promoted ? "ring-1 ring-fern/40" : "hover:ring-1 hover:ring-ink-300",
              )}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <span className={cls("pill border", CATEGORY_COLOR[r.category])}>
                  {CATEGORY_LABEL[r.category]}
                </span>
                <div className="flex items-center gap-1">
                  {isStatutory(r) ? (
                    <span
                      className="pill bg-foyr/10 text-foyr-deep border border-foyr/30 text-[10px] inline-flex items-center gap-0.5"
                      title={`Grounded in a published code · ${r.dataset ?? ""}`}
                    >
                      <ShieldCheck className="w-2.5 h-2.5" /> statutory
                    </span>
                  ) : null}
                  <span className={cls(
                    "pill",
                    r.severity === "block" ? "bg-foyr text-cream" :
                    r.severity === "warn"  ? "bg-foyr/10 text-foyr-deep" :
                                             "bg-cream-dark border border-border text-ink-700",
                  )}>
                    {r.severity}
                  </span>
                </div>
              </div>
              <div className="text-sm font-semibold text-ink-950 leading-snug">{r.title}</div>
              <div className="text-xs text-ink-700 leading-snug">{r.natural}</div>
              <div className="font-mono text-[11px] text-ink-700 leading-snug bg-cream-deeper rounded-lg px-2 py-1.5">
                {predicateToText(r.predicate)}
              </div>
              <div className="grid grid-cols-2 gap-1 text-[10px] text-ink-500">
                <span><span className="uppercase tracking-wider">on</span> <span className="font-mono text-ink-700">{r.trigger}</span></span>
                <span><span className="uppercase tracking-wider">→</span> <span className="font-mono text-ink-700">{r.action}</span></span>
              </div>
              <div className="text-[11px] text-ink-500 italic mt-auto">"{r.because}"</div>
              {r.dataset ? (
                <div className="text-[10px] font-mono text-ink-500/80">↳ {r.dataset}</div>
              ) : null}
              <div className="flex items-center justify-between mt-1 gap-2">
                <span className={cls(
                  "inline-flex items-center gap-1.5 text-[11px]",
                  selected ? "text-foyr" : promoted ? "text-fern" : "text-ink-500",
                )}>
                  <span className={cls(
                    "w-4 h-4 rounded grid place-items-center border",
                    selected ? "bg-foyr text-cream border-foyr" :
                    promoted ? "bg-fern/10 border-fern/40 text-fern" :
                               "border-border bg-cream-dark",
                  )}>
                    {selected || promoted ? <Check className="w-2.5 h-2.5" /> : null}
                  </span>
                  {selected ? "Selected" : promoted ? "Already watching" : "Tap to select"}
                </span>
                <button
                  type="button"
                  onClick={(ev) => { ev.stopPropagation(); onOpenInBuilder(r); }}
                  className="text-[11px] text-ink-500 hover:text-ink-950 inline-flex items-center gap-1"
                  title="Open in the builder to tweak before promote"
                >
                  <Wrench className="w-3 h-3" /> Open in builder
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function Header({ promotedCount }: { promotedCount: number }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-ink-950 text-cream grid place-items-center">
          <BookOpen className="w-4 h-4" />
        </span>
        <div>
          <div className="text-base font-semibold text-ink-950 leading-tight">Designer rule library</div>
          <div className="text-[12px] text-ink-500 leading-tight">
            {DESIGNER_RULES.length} typed rules. Tap to select · promote selection at the bottom of the page · open in builder to tweak.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <span className="pill bg-fern/10 text-fern tabular">{promotedCount} watching</span>
        <span className="pill bg-cream-dark border border-border text-ink-700 tabular">{DESIGNER_RULES.length} total</span>
      </div>
    </div>
  );
}
